import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { validateSimCard } from "../../../../validation/simCardValidation";
import classes from "./EditSimCardModal.module.scss";

const EditSimCardModal = ({
  t,
  show,
  onClose,
  onSave,
  simCard,
  tableType = "simCards",
}) => {
  const [validated, setValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    sim: "",
    phonenumber: "",
    pin: "",
    pin2: "",
    puk: "",
    puk2: "",
    cardname: "",
  });

  // Initialize form data when sim card changes
  useEffect(() => {
    if (simCard) {
      setFormData({
        sim: simCard.sim || "",
        phonenumber: simCard.phonenumber || "",
        pin: simCard.pin || "",
        pin2: simCard.pin2 || "",
        puk: simCard.puk || "",
        puk2: simCard.puk2 || "",
        cardname: simCard.cardname || "",
      });
    }
  }, [simCard]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clone formData to avoid modifying state directly
    const formDataToSubmit = { ...formData };

    // Validate the form data
    const errors = validateSimCard(formDataToSubmit);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError(t("please_fix_validation_errors"));
      return;
    }

    // Clear validation errors when form is valid
    setValidationErrors({});
    setError("");

    try {
      const result = await onSave(formDataToSubmit);
      // Check if submission was successful
      if (result && result.success === true) {
        setError("");
        onClose();
      }
    } catch (err) {
      setError(t("error_updating_product"));
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      className={classes.editSimCardModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("edit_product")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row>
            {/* SIM */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="sim">
                <Form.Label>{t("sim")}</Form.Label>
                <Form.Control
                  type="text"
                  name="sim"
                  value={formData.sim}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.sim}
                  className={`${
                    tableType ? `modal-${tableType}-field-sim` : ""
                  }`}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.sim
                    ? t(validationErrors.sim)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Phone Number */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="phonenumber">
                <Form.Label>{t("phoneNumber")}</Form.Label>
                <Form.Control
                  type="text"
                  name="phonenumber"
                  value={formData.phonenumber}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.phonenumber}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.phonenumber
                    ? t(validationErrors.phonenumber)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PIN */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="pin">
                <Form.Label>{t("pin")}</Form.Label>
                <Form.Control
                  type="text"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.pin}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.pin
                    ? t(validationErrors.pin)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PIN2 */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="pin2">
                <Form.Label>{t("pin2")}</Form.Label>
                <Form.Control
                  type="text"
                  name="pin2"
                  value={formData.pin2}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.pin2}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.pin2
                    ? t(validationErrors.pin2)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PUK */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="puk">
                <Form.Label>{t("puk")}</Form.Label>
                <Form.Control
                  type="text"
                  name="puk"
                  value={formData.puk}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.puk}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.puk
                    ? t(validationErrors.puk)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PUK2 */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="puk2">
                <Form.Label>{t("puk2")}</Form.Label>
                <Form.Control
                  type="text"
                  name="puk2"
                  value={formData.puk2}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.puk2}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.puk2
                    ? t(validationErrors.puk2)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Card Name */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="cardname">
                <Form.Label>{t("card_name")}</Form.Label>
                <Form.Control
                  type="text"
                  name="cardname"
                  value={formData.cardname}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.cardname}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.cardname
                    ? t(validationErrors.cardname)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {t("save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSimCardModal;
